import { Input, Switch, Tooltip } from 'antd'
import React from 'react'
import IronWebCatalogueStatus from 'src/screens/suppliers/IronWebCatalogueStatus'
import { isEmail, ReactMultiEmail } from 'react-multi-email'
import 'react-multi-email/style.css'
import styled from 'styled-components/macro'
import { optionalLabel } from 'src/utils/supplierFormFieldLabels'
import {
  ORDER_CUTOFF_INSTRUCTIONS_HELPER_TEXT,
  ORDER_CUTOFF_INSTRUCTIONS_LABEL,
  ORDER_CUTOFF_TIME_HELPER_TEXT,
  ORDER_CUTOFF_TIME_LABEL,
} from 'src/utils/supplierOrderCutOff'

const EditSupplierModal = ({
  supplier,
  setEditingSupplier,
  isPlatformAdmin = false,
}) => {
  const isIronWeb =
    String(supplier?.integrationType || '')
      .trim()
      .toLowerCase() === 'ironweb'

  const setExclusiveSupplierType = (type, checked) => {
    if (checked) {
      setEditingSupplier({
        ...supplier,
        TFRSupplier: type === 'TFR',
        GREENSupplier: type === 'GREEN',
        JUSTSupplier: type === 'JUST',
      })
    } else {
      setEditingSupplier({
        ...supplier,
        ...(type === 'TFR' && { TFRSupplier: false }),
        ...(type === 'GREEN' && { GREENSupplier: false }),
        ...(type === 'JUST' && { JUSTSupplier: false }),
      })
    }
  }

  return (
    <Wrapper>
      <div>
        Please note that updating the values below updates all venues in the
        database
      </div>
      <Row>
        <InputComp
          value={supplier.title}
          onChange={e => {
            setEditingSupplier({ ...supplier, title: e.target.value })
          }}
          addonBefore={'Title'}
          placeholder='Title'
        />
      </Row>
      <Row>
        <InputComp
          value={supplier.secondTitle}
          onChange={e => {
            setEditingSupplier({ ...supplier, secondTitle: e.target.value })
          }}
          addonBefore={'Second title'}
          placeholder='Second title'
        />
      </Row>
      <Row>
        <InputComp
          value={supplier.emailAddress}
          onChange={e => {
            setEditingSupplier({
              ...supplier,
              emailAddress: e.target.value.toLowerCase(),
            })
          }}
          addonBefore={'Order email address'}
          placeholder='Email address'
        />
      </Row>
      <Row>
        <Div>
          <Span>Second Email Addresses</Span>
          <ReactMultiEmail
            placeholder=''
            emails={
              supplier.secondEmailAddresses ? supplier.secondEmailAddresses : []
            }
            onChange={_emails => {
              setEditingSupplier({ ...supplier, secondEmailAddresses: _emails })
            }}
            validateEmail={
              email => isEmail(email) // return boolean
            }
            getLabel={(email, index, removeEmail) => (
              <InnerDiv data-tag key={index}>
                {email}
                <span data-tag-handle onClick={() => removeEmail(index)}>
                  x
                </span>
              </InnerDiv>
            )}
          />
        </Div>
      </Row>
      <Row>
        <InputComp
          value={supplier.phoneNumber}
          onChange={e => {
            setEditingSupplier({
              ...supplier,
              phoneNumber: e.target.value,
            })
          }}
          addonBefore={'Contact number'}
          placeholder='Contact number'
        />
      </Row>
      <Row>
        <InputComp
          value={supplier.vendorCode}
          onChange={e => {
            setEditingSupplier({
              ...supplier,
              vendorCode: e.target.value,
            })
          }}
          addonBefore={'Vendor Code'}
          placeholder='Vendor Code'
        />
      </Row>
      <Row>
        <InputComp
          value={supplier.integrationType}
          onChange={e => {
            setEditingSupplier({
              ...supplier,
              integrationType: e.target.value,
            })
          }}
          addonBefore={'Integration type'}
          placeholder='Integration type'
        />
      </Row>
      {isPlatformAdmin && isIronWeb && (
        <Row>
          <InputComp
            value={supplier.ironWebAgreementNumber || ''}
            onChange={e => {
              setEditingSupplier({
                ...supplier,
                ironWebAgreementNumber: e.target.value,
              })
            }}
            addonBefore={'IronWeb agreement number'}
            placeholder='Agreement number'
          />
          <AgreementHint>
            After you save a new agreement number, the next file for that number
            can import.
          </AgreementHint>
        </Row>
      )}
      {isIronWeb && (
        <IronWebCatalogueStatus
          priceFileId={supplier?._id}
          agreementNumber={supplier.ironWebAgreementNumber || ''}
        />
      )}
      <Row>
        <InputComp
          value={supplier.minOrderValue}
          onChange={e => {
            setEditingSupplier({ ...supplier, minOrderValue: e.target.value })
          }}
          addonBefore={'Minimum order value'}
          placeholder='Minimum order value'
        />
      </Row>
      <Row>
        <InputComp
          type='time'
          value={supplier.orderCutOffTime || ''}
          onChange={e => {
            setEditingSupplier({
              ...supplier,
              orderCutOffTime: e.target.value,
            })
          }}
          addonBefore={optionalLabel(ORDER_CUTOFF_TIME_LABEL)}
          placeholder='16:00'
        />
        <HelperText>{ORDER_CUTOFF_TIME_HELPER_TEXT}</HelperText>
      </Row>
      <Row>
        <InputComp
          value={supplier.orderCutOffInstructions || ''}
          onChange={e => {
            setEditingSupplier({
              ...supplier,
              orderCutOffInstructions: e.target.value,
            })
          }}
          addonBefore={optionalLabel(ORDER_CUTOFF_INSTRUCTIONS_LABEL)}
          placeholder='Order before 4pm for next day delivery'
        />
        <HelperText>{ORDER_CUTOFF_INSTRUCTIONS_HELPER_TEXT}</HelperText>
      </Row>
      <Row>
        <InputComp
          value={supplier.logoUrl}
          onChange={e => {
            setEditingSupplier({ ...supplier, logoUrl: e.target.value })
          }}
          addonBefore={'Logo URL'}
          placeholder='Logo URL'
        />
      </Row>

      <Row>
        <InputComp
          value={supplier.dc}
          onChange={e => {
            setEditingSupplier({ ...supplier, dc: e.target.value })
          }}
          addonBefore={'DC'}
          placeholder='Not Applicable'
        />
      </Row>

      <Row>
        <SwitchSpan>Allows credit purchase</SwitchSpan>
        <SwitchComp
          value={supplier.allowsCreditPurchase}
          onChange={e => {
            setEditingSupplier({ ...supplier, allowsCreditPurchase: e })
          }}
          checked={supplier.allowsCreditPurchase}
        />
      </Row>
      {isPlatformAdmin && (
        <>
          <Row>
            <SwitchSpan>TFR</SwitchSpan>
            <SwitchComp
              value={supplier?.TFRSupplier}
              onChange={checked => setExclusiveSupplierType('TFR', checked)}
              checked={supplier?.TFRSupplier}
            />
          </Row>
          <Row>
            <SwitchSpan>GREEN</SwitchSpan>
            <SwitchComp
              value={supplier?.GREENSupplier}
              onChange={checked => setExclusiveSupplierType('GREEN', checked)}
              checked={supplier?.GREENSupplier}
            />
          </Row>
          <Row>
            <SwitchSpan>JUST</SwitchSpan>
            <SwitchComp
              value={supplier?.JUSTSupplier}
              onChange={checked => setExclusiveSupplierType('JUST', checked)}
              checked={supplier?.JUSTSupplier}
            />
          </Row>
        </>
      )}
      <Row>
        <SwitchSpan>Default Supplier</SwitchSpan>
        <SwitchComp
          value={supplier?.defaultSupplier}
          onChange={e => {
            setEditingSupplier({ ...supplier, defaultSupplier: e })
          }}
          checked={supplier?.defaultSupplier}
        />
      </Row>
      <Row>
        <Tooltip title='If enabled, all orders sent to this supplier will automatically be marked as approved after sending. Use this for suppliers with bots or automatic integrations.'>
          <SwitchSpan>Auto Approve Orders After Sending</SwitchSpan>
        </Tooltip>
        <SwitchComp
          value={supplier?.autoApproveOrders}
          onChange={e => {
            setEditingSupplier({ ...supplier, autoApproveOrders: e })
          }}
          checked={supplier?.autoApproveOrders}
        />
      </Row>
      <Row>
        <Tooltip title='If enabled, product comments will be disabled for this supplier.'>
          <SwitchSpan>Disable Comments</SwitchSpan>
        </Tooltip>
        <SwitchComp
          value={supplier?.disableProductComments ?? false}
          onChange={e => {
            setEditingSupplier({ ...supplier, disableProductComments: e })
          }}
          checked={supplier?.disableProductComments ?? false}
        />
      </Row>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const InputComp = styled(Input)``

const Row = styled.div`
  margin: 15px 0;
`

const AgreementHint = styled.p`
  margin: 6px 0 0;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
`

const HelperText = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
`

const SwitchComp = styled(Switch)``

const SwitchSpan = styled.span`
  font-weight: bold;
  display: inline-block;
  margin-right: 7px;
`

const InnerDiv = styled.div`
  background-color: ${({ theme }) => theme.colours.lettuceGreen} !important;
`
const Span = styled.span`
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  background-color: ${({ theme }) => theme.colours.bgGrey};
`
const Div = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  .react-multi-email {
    border: 1px solid ${({ theme }) => theme.colours.panSilver};
    border-radius: 0;
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  .react-multi-email:hover,
  .react-multi-email.focused {
    border: 1px solid #b5d922 !important;
  }
`

export default EditSupplierModal
